import { useEffect, useMemo, useState } from "react";
import { tenantService, execService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { compactCurrency } from "@/lib/currency";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDownRight, Minus, X, Loader2, ExternalLink } from "lucide-react";

export const SEV_STYLE = {
  critical: "text-red-600 bg-red-500/10 border-red-500/30",
  high: "text-amber-600 bg-amber-500/10 border-amber-500/30",
  medium: "text-sky-600 bg-sky-500/10 border-sky-500/30",
  low: "text-muted-foreground bg-muted/40 border-border",
};

export const fmtValue = (v, fmt) => {
  if (v == null) return "—";
  if (fmt === "currency") return compactCurrency(v);
  if (fmt === "percent") return `${v}%`;
  return new Intl.NumberFormat().format(v);
};
export const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return s; }
};

export function useTenantScope() {
  const { user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  useEffect(() => {
    if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {});
  }, [isSuper]);
  const params = useMemo(() => (tenantId ? { tenantId } : {}), [tenantId]);
  return { isSuper, tenants, tenantId, setTenantId, params };
}

export function TenantPicker({ isSuper, tenants, tenantId, setTenantId }) {
  if (!isSuper) return null;
  return (
    <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}
      className="bg-background border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" data-testid="exec-tenant-select">
      <option value="">Select tenant…</option>
      {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
    </select>
  );
}

const PERIODS = [["7d", "7D"], ["30d", "30D"], ["90d", "90D"], ["qtd", "QTD"]];
export function PeriodSelector({ period, setPeriod }) {
  return (
    <div className="flex bg-muted/40 rounded-lg p-1" data-testid="period-selector">
      {PERIODS.map(([v, l]) => (
        <button key={v} onClick={() => setPeriod(v)}
          className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${period === v ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
          data-testid={`period-${v}`}>{l}</button>
      ))}
    </div>
  );
}

export function SelectTenantNotice() {
  return (
    <div className="bg-card border border-border rounded-xl p-12 text-center" data-testid="select-tenant-notice">
      <h3 className="text-base font-semibold text-foreground">Select a tenant</h3>
      <p className="text-sm text-muted-foreground mt-1">Choose a tenant above to view executive insights.</p>
    </div>
  );
}

export function ForbiddenNotice() {
  return (
    <div className="bg-card border border-border rounded-xl p-12 text-center" data-testid="exec-forbidden">
      <h3 className="text-base font-semibold text-foreground">Access restricted</h3>
      <p className="text-sm text-muted-foreground mt-1">You don't have permission to view this executive dashboard. Contact your administrator for access.</p>
    </div>
  );
}

export function PageHeader({ icon: Icon, title, subtitle, children }) {
  return (
    <header className="flex items-start justify-between gap-4 mb-6 flex-wrap">
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          {Icon && <Icon className="h-5 w-5" />}
        </span>
        <div>
          <h1 className="text-xl font-bold text-foreground">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">{children}</div>
    </header>
  );
}

function Delta({ delta }) {
  if (!delta || delta.value == null) return null;
  const up = delta.dir === "up", down = delta.dir === "down";
  const Icon = up ? ArrowUpRight : down ? ArrowDownRight : Minus;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${up ? "text-emerald-600" : down ? "text-red-600" : "text-muted-foreground"}`}>
      <Icon className="h-3 w-3" />{Math.abs(delta.value)}%
    </span>
  );
}

export function KpiTile({ tile, onDrill }) {
  const clickable = !!tile.drilldown;
  return (
    <button type="button" onClick={clickable ? () => onDrill(tile.drilldown, tile.label) : undefined}
      className={`bg-card border border-border rounded-xl p-4 text-left w-full ${clickable ? "hover:border-primary/40 cursor-pointer" : "cursor-default"} transition-colors`}
      data-testid={`kpi-${tile.key}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{tile.label}</span>
        <Delta delta={tile.delta} />
      </div>
      <p className="text-2xl font-bold text-foreground mt-2">{fmtValue(tile.value, tile.format)}</p>
      {tile.hint && <p className="text-[11px] text-muted-foreground mt-1">{tile.hint}</p>}
      {clickable && <span className="text-[11px] text-primary font-semibold mt-1 inline-flex items-center gap-0.5">View records <ExternalLink className="h-3 w-3" /></span>}
    </button>
  );
}

export function Distribution({ title, data, testid }) {
  const entries = Object.entries(data || {});
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div className="bg-card border border-border rounded-xl p-5" data-testid={testid}>
      <h2 className="text-sm font-bold text-foreground mb-3">{title}</h2>
      {entries.length === 0 ? <p className="text-xs text-muted-foreground">No data.</p> : (
        <div className="space-y-2">
          {entries.sort((a, b) => b[1] - a[1]).map(([k, v]) => (
            <div key={k}>
              <div className="flex items-center justify-between text-xs mb-1"><span className="text-foreground capitalize">{k}</span><span className="text-muted-foreground font-semibold">{v}</span></div>
              <div className="h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${(v / max) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const REC_FIELDS = ["name", "customerName", "fullName", "subject", "email", "company", "stage", "status", "priority", "amount", "band", "expectedCloseDate"];
export function DrilldownPanel({ metric, label, params, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    execService.drilldown(metric, params).then(setData).catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [metric]);
  const items = data?.items || [];
  const cols = items.length ? REC_FIELDS.filter((f) => items.some((r) => r[f] != null)) : [];
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="drilldown-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-background border-l border-border w-full max-w-2xl h-full overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-background">
          <div>
            <h3 className="text-sm font-bold text-foreground">{label}</h3>
            <p className="text-[11px] text-muted-foreground">{data?.total ?? 0} record(s) · {data?.collection || metric}</p>
          </div>
          <button onClick={onClose} data-testid="drilldown-close"><X className="h-4 w-4 text-muted-foreground hover:text-foreground" /></button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : items.length === 0 ? (
          <p className="p-8 text-sm text-muted-foreground text-center">No records for this metric.</p>
        ) : (
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase text-muted-foreground border-b border-border">{cols.map((c) => <th key={c} className="px-3 py-2">{c}</th>)}</tr></thead>
            <tbody>
              {items.map((r, i) => (
                <tr key={r.id || i} className="border-b border-border last:border-0" data-testid={`drill-row-${i}`}>
                  {cols.map((c) => <td key={c} className="px-3 py-2 text-foreground truncate max-w-[160px]">{c === "amount" ? fmtValue(r[c], "currency") : String(r[c] ?? "—")}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
